import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right.svg";
import { RegionGroupedReturnsCenterSelector } from "@redotech/redo-merchant-managed-returns-app/returns-center-region-selector";
import { RedoManagedReturnsRpcClientContext } from "@redotech/redo-merchant-managed-returns-app/rpc-client";
import { type RedoManagedLocationState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RateSourceSelection } from "./rate-source-selection/rate-source-selection";
import * as css from "./redo-managed-location.module.css";
import {
  bulkToggleReturnsCenterIds,
  toggleReturnsCenterId,
} from "./returns-center-ids-utils";

export const RedoManagedLocationDetails = memo(
  function RedoManagedLocationDetails({
    state,
    setState,
  }: StepTypeDetailsProps<RedoManagedLocationState>) {
    const { t } = useTranslation();
    const rpcClient = useRequiredContext(RedoManagedReturnsRpcClientContext);
    const { teamId } = useParams<{ teamId: string }>();
    const navigate = useNavigate();

    const returnsCentersLoad = useLoad(
      async (signal) => {
        const result = await rpcClient.getReturnsCenters({}, { signal });
        return result.returnsCenters;
      },
      [rpcClient],
    );

    const centers = useMemo(
      () => returnsCentersLoad.value ?? [],
      [returnsCentersLoad.value],
    );

    const selectedIds = useMemo(
      () => new Set(state.returnsCenterIds ?? []),
      [state.returnsCenterIds],
    );

    const toggleSingle = useCallback(
      (id: string) => {
        setState((prev) => ({
          ...prev,
          returnsCenterIds: toggleReturnsCenterId(prev.returnsCenterIds, id),
        }));
      },
      [setState],
    );

    const bulkToggle = useCallback(
      (centerIds: string[], select: boolean) => {
        setState((prev) => ({
          ...prev,
          returnsCenterIds: bulkToggleReturnsCenterIds(
            prev.returnsCenterIds,
            centerIds,
            select,
          ),
        }));
      },
      [setState],
    );

    return (
      <div className={css.panel}>
        <div className={css.section}>
          <div className={css.fieldHeader}>
            <label className={css.fieldLabel}>{t("Returns centers")}</label>
            <Text
              as="span"
              fontSize="xs"
              fontWeight="medium"
              textColor="secondary"
            >
              {t("{{count}} selected", { count: selectedIds.size })}
            </Text>
          </div>
          <div className={css.fieldHelp}>
            {t(
              "Returns are routed to the closest available center based on the customer's address.",
            )}
          </div>

          {centers.length === 0 && !returnsCentersLoad.pending ? (
            <div className={css.empty}>
              {t("No returns centers are available to your team yet.")}
            </div>
          ) : (
            <RegionGroupedReturnsCenterSelector
              allCenters={centers}
              onBulkToggle={bulkToggle}
              onToggleSingle={toggleSingle}
              selectedIds={selectedIds}
            />
          )}
          <RedoButton
            hierarchy="link"
            IconTrailing={ArrowRightIcon}
            onClick={() =>
              teamId && navigate(`/stores/${teamId}/settings/warehouse`)
            }
            size="sm"
            text={t("Configure returns centers")}
          />

          <RateSourceSelection
            rateSourceSelection={state.rateSourceSelection}
            updateRateSourceSelection={(rateSourceSelection) => {
              setState((prev) => ({ ...prev, rateSourceSelection }));
            }}
          />
        </div>
      </div>
    );
  },
);
